import React, { useEffect, useState } from 'react';
import { X, Send, Upload, FileText, Plus, FolderOpen, Mail, Link } from 'lucide-react';
import axios from 'axios';
import { SharePointURL, EmailDocument } from '../types/kt-types.ts';
import { getToken } from '../auth/authConfig.jsx';

interface NewKTProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => void;
  selectedProject?: any;
  employeeDetails: any;
}

const NewKTProjectModal: React.FC<NewKTProjectModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  employeeDetails,
  selectedProject = {} 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newKTForm, setNewKTForm] = useState({
    projectName: '',
    role: selectedProject.Role || '',
    department: selectedProject.KT_Department || '',
    startDate: selectedProject.KtSessionStartDate || '',
    endDate: selectedProject.KtSessionEndDate || '',
    description: '',
    manager: selectedProject.KT_Manager || '',
    keyResponsibilities: '',
    criticalProjects: ''
  });

  // console.log("newKTForm selectedProject", newKTForm);

  useEffect(() => {
    if (selectedProject) {
      setNewKTForm({
        projectName: selectedProject.ProjectName || '',
        role: selectedProject.RoleForKT || '',
        department: selectedProject.Department || '',
        startDate: selectedProject.KtSessionStartDate ? new Date(selectedProject.KtSessionStartDate).toISOString().split('T')[0] : '',
        endDate: selectedProject.KtSessionEndDate ? new Date(selectedProject.KtSessionEndDate).toISOString().split('T')[0] : '',
        description: selectedProject.ProjectDescription || '',
        manager: selectedProject.manager_email || '',
        keyResponsibilities: selectedProject.KeyResponsibilities || '',
        criticalProjects: selectedProject.CriticalProjectsInfo || ''
      });
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject?.SharePointLinks) {
      try {
        const links = JSON.parse(selectedProject.SharePointLinks);
        setSharepointUrls(links);
      } catch (err) {
        console.error("Error parsing SharePointLinks", err);
      }
    }
  }, [selectedProject]);


  useEffect(() => {
    if (selectedProject?.EmailDocs) {
      try {
        const emails = JSON.parse(selectedProject.EmailDocs);
        setEmailDocuments(emails);
      } catch (err) {
        console.error("Error parsing EmailDocs", err);
      }
    }
  }, [selectedProject]);
  
  useEffect(() => {
    if (selectedProject?.documents && Array.isArray(selectedProject.documents)) {
      // Assuming you're not downloading file data yet, just storing metadata
      const filesMeta = selectedProject.documents.map((doc) => ({
        name: doc.name,
        path: doc.path,
        uploadedBy: doc.uploaded_by,
        uploadTime: doc.upload_time,
        reviewStatus: doc.review_status,
      }));
  
      setUploadedDocuments(filesMeta); // You may rename this to setUploadedDocumentMeta
    }
  }, [selectedProject]);
  console.log("NewKTProjectModal selectedProject", selectedProject);

  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);
  const [sharepointUrls, setSharepointUrls] = useState<SharePointURL[]>([]);
  const [emailDocuments, setEmailDocuments] = useState<EmailDocument[]>([]);
  const [newSharepointUrl, setNewSharepointUrl] = useState({ name: '', url: '', description: '' });
  const [newEmailDoc, setNewEmailDoc] = useState({ subject: '', sender: '', date: '', description: '' });

  if (!isOpen) return null;

  const handleKTFormChange = (field: string, value: string) => {
    setNewKTForm(prev => ({ ...prev, [field]: value }));
  };

  const handleKTDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setUploadedDocuments(prev => [...prev, ...files]);
  };

  const removeUploadedDocument = (index: number) => {
    setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const addSharepointUrl = () => {
    if (newSharepointUrl.name && newSharepointUrl.url) {
      const newUrl: SharePointURL = {
        id: Date.now().toString(),
        ...newSharepointUrl
      };
      setSharepointUrls(prev => [...prev, newUrl]);
      setNewSharepointUrl({ name: '', url: '', description: '' });
    }
  };

  const removeSharepointUrl = (id: string) => {
    setSharepointUrls(prev => prev.filter(url => url.id !== id));
  };

  const addEmailDocument = () => {
    if (newEmailDoc.subject && newEmailDoc.sender) {
      const newEmail: EmailDocument = {
        id: Date.now().toString(),
        ...newEmailDoc
      };
      setEmailDocuments(prev => [...prev, newEmail]);
      setNewEmailDoc({ subject: '', sender: '', date: '', description: '' });
    }
  };

  const removeEmailDocument = (id: string) => {
    setEmailDocuments(prev => prev.filter(email => email.id !== id));
  };

  // Convert files to base64 string
  const convertFilesToBase64 = async (files: File[]): Promise<string[]> => {
    const base64Files = await Promise.all(
      files.map(file => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === 'string') {
              // Remove the data:mime;base64, prefix
              const base64 = reader.result.split(',')[1];
              resolve(base64);
            } else {
              reject(new Error('Failed to convert file to base64'));
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    );
    return base64Files;
  };

  // Format SharePoint URLs according to API structure
  const formatSharePointLinks = () => {
    console.log('Formatting SharePoint URLs:', sharepointUrls);
    return sharepointUrls.map(url => ({
      name: url.name,
      url: url.url,
      description: url.description || ''
    }));
  };

  // Format Email Documents according to API structure
  const formatEmailDocuments = () => {
    console.log('Formatting Email Documents:', emailDocuments);
    return emailDocuments.map(email => ({
      subject: email.subject,
      sender_recipient: email.sender,
      date: email.date || new Date().toISOString().split('T')[0],
      description: email.description || ''
    }));
  };

  // Validate required fields
  const validateForm = (): boolean => {
    const requiredFields = [
      'projectName', 'description' , 'keyResponsibilities', 'criticalProjects'
    ];
    
    return requiredFields.every(field => 
      newKTForm[field as keyof typeof newKTForm]?.trim() !== ''
    );
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      alert('Please fill in all required fields');
      return;
    }
  
    setIsSubmitting(true);
    const token = await getToken();
  
    try {


      const convertFilesToBase64 = (files) => {
        const filePromises = files
          .filter(file => file instanceof File)
          .map(file => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const base64String = reader.result.split(',')[1];
                resolve({ name: file.name, base64: base64String });
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
          });
      
        const metadataDocuments = files.filter(file => !(file instanceof File));
      
        return Promise.all(filePromises).then(base64Docs => [...base64Docs, ...metadataDocuments]);
      };
      
      
      const documentList = await convertFilesToBase64(uploadedDocuments);
  
      // 📦 Prepare payload
      const payload = {
        employee_id: selectedProject.employee_id,
        ProjectName: newKTForm.projectName,
        Role: newKTForm.role,
        Department: newKTForm.department,
        Manager: newKTForm.manager,
        StartDate: newKTForm.startDate,
        EndDate: newKTForm.endDate,
        ProjectDescription: newKTForm.description,
        KeyResponsibilities: newKTForm.keyResponsibilities,
        CriticalProjectsInfo: newKTForm.criticalProjects,
        DocumentList: documentList, // ✅ as array of {name, base64}
        SharePointLinks: formatSharePointLinks(),
        EmailDocs: formatEmailDocuments(),
        CreatedDate: new Date().toISOString(),
        Status: 'Draft',
        taskId: selectedProject.task_id || '',
      };
  
      const baseurl = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/project_kt`;
  
      const response = await axios.post(baseurl, payload, {
        headers: {
          'oidc-token': token,
          'Content-Type': 'application/json'
        }
      });
  
      if(response.status === 200 || response.status === 201) {
        console.log('✅ KT Project created successfully:', response.data);
        setIsSubmitting(false);
        setNewKTForm({
          projectName: '',
          role: selectedProject.Role || '',
          department: selectedProject.KT_Department || '',
          startDate: '',
          endDate: '',
          description: '',
          manager: selectedProject.KT_Manager || '',
          keyResponsibilities: '',
          criticalProjects: ''
        });
        setUploadedDocuments([]);
        setSharepointUrls([]);
        setEmailDocuments([]);
        onClose();
        employeeDetails()
      }
  
    } catch (error) {
      setIsSubmitting(false);
      alert('SOMETHING WENT WRONG! Please try again later.');
      // onClose();
      // setUploadedDocuments([]);
      // setSharepointUrls([]);
      // setEmailDocuments([]);
  
  
      await onSubmit({ success: false, error });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  

  return (
    <div style={{zIndex:"99999"}} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Create New KT Project</h2>
            <p className="text-gray-600">Fill in the details for your knowledge transfer project</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Name <span style={{color:"red",fontSize:"16px"}}>*</span>
                </label>
                <input
                  type="text"
                  value={newKTForm.projectName}
                  onChange={(e) => handleKTFormChange('projectName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Financial Analysis KT - Q1 2024"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role 
                </label>
                <input
                  type="text"
                  value={newKTForm.role}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Senior Financial Analyst"
                  disabled={true}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department 
                </label>
                <input
                  type="text"
                  value={newKTForm.department}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Finance"
                  disabled={true}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Manager 
                </label>
                <input
                  type="text"
                  value={newKTForm.manager}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., John Smith"
                  disabled={true}
                />
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date  {newKTForm.startDate}
                </label>
                <input
                  type="date"
                  value={newKTForm.startDate}
                  onChange={(e) => handleKTFormChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={true}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date 
                </label>
                <input
                  type="date"
                  value={newKTForm.endDate}
                  onChange={(e) => handleKTFormChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={true}
                />
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Description  <span style={{color:"red",fontSize:"16px"}}>*</span>
                </label>
                <textarea
                  value={newKTForm.description}
                  onChange={(e) => handleKTFormChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the knowledge transfer project and its objectives..."
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key Responsibilities  <span style={{color:"red",fontSize:"16px"}}>*</span>
                </label>
                <textarea
                  value={newKTForm.keyResponsibilities}
                  onChange={(e) => handleKTFormChange('keyResponsibilities', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="List the key responsibilities that need to be transferred..."
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Critical Projects & Information  <span style={{color:"red",fontSize:"16px"}}>*</span>
                </label>
                <textarea
                  value={newKTForm.criticalProjects}
                  onChange={(e) => handleKTFormChange('criticalProjects', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe critical ongoing projects and important information..."
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Document Upload Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Upload</h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <div className="mb-2">
                <label htmlFor="kt-document-upload" className="cursor-pointer">
                  <span className="text-blue-600 hover:text-blue-700 font-medium">Click to upload documents</span>
                  <span className="text-gray-600"> or drag and drop</span>
                </label>
                <input
                  id="kt-document-upload"
                  type="file"
                  multiple
                  onChange={handleKTDocumentUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  disabled={isSubmitting}
                />
              </div>
              <p className="text-xs text-gray-500">PDF, DOC, XLS, PPT files up to 10MB each</p>
            </div>
            
            {uploadedDocuments.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Uploaded Documents:</h4>
                {uploadedDocuments.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{doc.name}</span>
                    </div>
                    <button
                      onClick={() => removeUploadedDocument(index)}
                      className="text-red-600 hover:text-red-700"
                      disabled={isSubmitting}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SharePoint URLs Section */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <FolderOpen className="w-6 h-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">SharePoint URLs</h3>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document/Folder Name
                  </label>
                  <input
                    type="text"
                    value={newSharepointUrl.name}
                    onChange={(e) => setNewSharepointUrl(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Financial Reports Folder"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SharePoint URL
                  </label>
                  <input
                    type="url"
                    value={newSharepointUrl.url}
                    onChange={(e) => setNewSharepointUrl(prev => ({ ...prev, url: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://company.sharepoint.com/..."
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newSharepointUrl.description}
                  onChange={(e) => setNewSharepointUrl(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of the content..."
                  disabled={isSubmitting}
                />
              </div>
              <button
                onClick={addSharepointUrl}
                disabled={!newSharepointUrl.name || !newSharepointUrl.url || isSubmitting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add SharePoint URL
              </button>
            </div>

            {sharepointUrls.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Added SharePoint URLs:</h4>
                {sharepointUrls.map((url) => (
                  <div key={url.id} className="flex items-start justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Link className="w-4 h-4 text-blue-600 mt-1" />
                      <div>
                        <div className="font-medium text-blue-900">{url.name}</div>
                        <div className="text-sm text-blue-700 break-all">{url.url}</div>
                        {url.description && (
                          <div className="text-sm text-blue-600 mt-1">{url.description}</div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeSharepointUrl(url.id)}
                      className="text-red-600 hover:text-red-700"
                      disabled={isSubmitting}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Email Documents Section */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Email Documents</h3>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Subject
                  </label>
                  <input
                    type="text"
                    value={newEmailDoc.subject}
                    onChange={(e) => setNewEmailDoc(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Q4 Budget Approval"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sender/Recipient
                  </label>
                  <input
                    type="text"
                    value={newEmailDoc.sender}
                    onChange={(e) => setNewEmailDoc(prev => ({ ...prev, sender: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., john.doe@company.com"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={newEmailDoc.date}
                    onChange={(e) => setNewEmailDoc(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newEmailDoc.description}
                    onChange={(e) => setNewEmailDoc(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Brief description of email content..."
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <button
                onClick={addEmailDocument}
                disabled={!newEmailDoc.subject || !newEmailDoc.sender || isSubmitting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Email Document
              </button>
            </div>

            {emailDocuments.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Added Email Documents:</h4>
                {emailDocuments.map((email) => (
                  <div key={email.id} className="flex items-start justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Mail className="w-4 h-4 text-green-600 mt-1" />
                      <div>
                        <div className="font-medium text-green-900">{email.subject}</div>
                        <div className="text-sm text-green-700">From/To: {email.sender}</div>
                        {email.date && (
                          <div className="text-sm text-green-600">Date: {email.date}</div>
                        )}
                        {email.description && (
                          <div className="text-sm text-green-600 mt-1">{email.description}</div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeEmailDocument(email.id)}
                      className="text-red-600 hover:text-red-700"
                      disabled={isSubmitting}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!validateForm() || isSubmitting}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit & Notify Manager
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewKTProjectModal;